import { useMaterialImageUrl } from "@frontend/pages/case-workspace/pathology/hooks/use-material-image"
import type { CaseSupportingMaterial } from "@frontend/pages/case-workspace/pathology/types"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

function MaterialImage({ caseId, material }: { caseId: string; material: CaseSupportingMaterial }) {
  const url = useMaterialImageUrl(caseId, material.id)
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const isPanning = useRef(false)
  const lastPointer = useRef({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    function onWheel(e: WheelEvent) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      setScale((s) => Math.min(Math.max(s * delta, 0.1), 20))
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    isPanning.current = true
    lastPointer.current = { x: e.clientX, y: e.clientY }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isPanning.current) return
    const dx = e.clientX - lastPointer.current.x
    const dy = e.clientY - lastPointer.current.y
    lastPointer.current = { x: e.clientX, y: e.clientY }
    setTranslate((t) => ({ x: t.x + dx, y: t.y + dy }))
  }, [])

  const handlePointerUp = useCallback(() => {
    isPanning.current = false
  }, [])

  const handleDoubleClick = useCallback(() => {
    setScale(1)
    setTranslate({ x: 0, y: 0 })
  }, [])

  if (!url) {
    return (
      <div className="flex items-center justify-center h-48 bg-muted rounded-md text-muted-foreground text-xs">
        Loading...
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="overflow-hidden rounded-md cursor-grab active:cursor-grabbing select-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
    >
      <img
        src={url}
        alt={material.label || material.filename}
        draggable={false}
        className="max-w-full max-h-[80vh] object-contain"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: "center center"
        }}
      />
    </div>
  )
}

function formatMaterialType(type: string): string {
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export function SupportingMaterialViewer({
  materials,
  caseId
}: {
  materials: CaseSupportingMaterial[]
  caseId: string
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, CaseSupportingMaterial[]>()
    for (const m of materials) {
      const list = map.get(m.material_type) ?? []
      list.push(m)
      map.set(m.material_type, list)
    }
    return map
  }, [materials])

  return (
    <div className="space-y-6">
      {[...grouped.entries()].map(([type, items]) => (
        <div key={type} className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">{formatMaterialType(type)}</h3>
          <div className="flex flex-wrap gap-4">
            {items.map((material) => (
              <div key={material.id} className="space-y-1">
                <MaterialImage caseId={caseId} material={material} />
                {material.label && <p className="text-xs text-muted-foreground">{material.label}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
